import { useState } from "react";
import { X, TrendingUp, ChevronDown } from "lucide-react";
import { TrendingTag } from "@/features/trending/types/TrendingTags";
import { useAuth } from "@/auth/useAuth";

interface DayData {
  trendingTags: TrendingTag[];
  articleTitles: string[];
}

interface CliffNote {
  main_point?: string;
  why_care?: string;
  key_facts?: Array<{ fact: string; source: string }>;
  whats_next?: string[];
}

const parseThis = (cliffNoteStr: string | null) => {
  if (!cliffNoteStr) return null;
  try {
    const parsed = JSON.parse(cliffNoteStr);
    // If parsed is a string, it might be double-encoded JSON
    if (typeof parsed === "string") {
      try {
        const doubleDecoded = JSON.parse(parsed);
        console.log("double-decoded:", doubleDecoded);
        return doubleDecoded;
      } catch (innerError) {
        console.error("Failed to double-decode:", innerError);
      }
    }
    return parsed;
  } catch (error) {
    console.error("Error parsing cliff note:", error);
    return null;
  }
};

interface DayTrendingPanelProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDate: Date | null;
  selectedDayData: DayData | null;
  isLoadingDayData: boolean;
  selectedCliffNote: TrendingTag | null;
  onCliffNoteChange: (tag: TrendingTag | null) => void;
}

export default function DayTrendingPanel({
  isOpen,
  onClose,
  selectedDate,
  selectedDayData,
  isLoadingDayData,
  selectedCliffNote,
  onCliffNoteChange,
}: DayTrendingPanelProps) {
  const { session } = useAuth();
  const [showArticles, setShowArticles] = useState(false);
  const handleClose = () => {
    onCliffNoteChange(null);
    onClose();
  };

  const handleTagClick = (tag: TrendingTag) => {
    setShowArticles(false); // Reset articles view when switching tags
    onCliffNoteChange(tag);
  };

  // Parse cliff note like TrendingTags does
  const getParsedCliffNote = (tag: TrendingTag): CliffNote | null => {
    if (typeof tag.cliff_note === "string") {
      return parseThis(tag.cliff_note);
    } else if (tag.cliff_note !== null && typeof tag.cliff_note === "object") {
      return tag.cliff_note as CliffNote;
    }
    return null;
  };

  if (!isOpen) return null;

  return (
    <div className="w-full max-w-4xl mx-auto mb-6">
      {/* Panel */}
      <div className="bg-white rounded-lg border border-gray-300 shadow-md p-6 transform transition-all duration-300 ease-out">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              AI News for{" "}
              {selectedDate
                ? selectedDate.toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                : "Trending Topics"}
            </h3>
            <p className="text-sm text-gray-500">
              {selectedDayData?.articleTitles.length || 0} articles summarized
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto">
          {isLoadingDayData ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-gray-500">Loading trending topics...</div>
            </div>
          ) : selectedDayData?.trendingTags.length ? (
            <div className="space-y-6">
              {/* Trending Tags - Always Visible */}
              <div>
                <div className="flex flex-wrap gap-2">
                  {selectedDayData.trendingTags.map((tag) => (
                    <button
                      key={tag.tag}
                      onClick={() => handleTagClick(tag)}
                      className={`px-3 py-2 rounded-full text-sm font-medium border transition-all duration-200 hover:scale-105 flex items-center ${
                        selectedCliffNote?.tag === tag.tag
                          ? "bg-black text-white border-black"
                          : "bg-gradient-to-r from-blue-100 to-purple-100 hover:from-blue-200 hover:to-purple-200 text-gray-800 border-gray-300"
                      }`}
                    >
                      <TrendingUp
                        className={`w-4 h-4 mr-2 ${
                          selectedCliffNote?.tag === tag.tag
                            ? "text-white"
                            : "text-gray-600"
                        }`}
                      />
                      <span className="font-semibold">{tag.tag}</span>
                      {tag.articles && (
                        <span className="ml-1 text-xs opacity-75">
                          ({tag.articles.length})
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Cliff Note - Show when tag is selected */}
              {selectedCliffNote && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <h4 className="text-lg font-semibold text-gray-900">
                      {selectedCliffNote.tag}
                    </h4>
                  </div>

                  {/* Use same cliff note display as TrendingTags */}
                  {(() => {
                    const parsedCliffNote =
                      getParsedCliffNote(selectedCliffNote);
                    return parsedCliffNote ? (
                      <div className="bg-white rounded-lg p-3">
                        <div className="space-y-2">
                          <h4 className="text-md font-semibold mb-2">
                            Big Picture
                          </h4>
                          <p className="text-sm text-gray-800">
                            {parsedCliffNote.main_point}
                          </p>

                          <h4 className="text-md font-semibold mt-4">
                            Why Care
                          </h4>
                          <p className="text-sm text-gray-800">
                            {parsedCliffNote.why_care}
                          </p>
                        </div>

                        <div className="mt-4 space-y-4">
                          {parsedCliffNote.key_facts &&
                            parsedCliffNote.key_facts.length > 0 && (
                              <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-indigo-300 rounded-md p-4">
                                <h4 className="font-bold text-sm mb-2 text-indigo-800">
                                  Key Facts
                                </h4>
                                <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
                                  {parsedCliffNote.key_facts.map(
                                    (item, idx) => (
                                      <li key={idx}>{item.fact}</li>
                                    )
                                  )}
                                </ul>
                              </div>
                            )}

                          {parsedCliffNote.whats_next &&
                            parsedCliffNote.whats_next.length > 0 && (
                              <div className="mt-4 space-y-4">
                                <h4 className="text-md font-semibold mt-4 text-gray-800">
                                  What's likely next
                                </h4>
                                <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
                                  {parsedCliffNote.whats_next.map(
                                    (item, idx) => (
                                      <li key={idx}>{item}</li>
                                    )
                                  )}
                                </ul>
                              </div>
                            )}
                        </div>

                        {/* Article Links Section */}
                        {selectedCliffNote.articles &&
                          selectedCliffNote.articles.length > 0 && (
                            <div className="mt-6 pt-4 border-t border-gray-200">
                              <div className="flex justify-end">
                                <button
                                  onClick={() =>
                                    setShowArticles((prev: boolean) => !prev)
                                  }
                                  className="text-sm cursor-pointer font-medium text-gray-600 hover:underline transition-all inline-flex items-center"
                                >
                                  {(() => {
                                    const uniqueAuthors = [
                                      ...new Set(
                                        selectedCliffNote.articles
                                          .map((item) => item.author)
                                          .filter(Boolean)
                                      ),
                                    ];
                                    const displayAuthors = uniqueAuthors.slice(
                                      0,
                                      3
                                    );

                                    if (uniqueAuthors.length === 0) {
                                      return "As reported by multiple sources";
                                    } else if (uniqueAuthors.length === 1) {
                                      return `As reported by ${displayAuthors[0]}`;
                                    } else if (uniqueAuthors.length <= 3) {
                                      return `As reported by ${displayAuthors.join(
                                        ", "
                                      )}`;
                                    } else {
                                      return `Getting attention on ${displayAuthors.join(
                                        ", "
                                      )} and ${uniqueAuthors.length - 3} more`;
                                    }
                                  })()}
                                  <ChevronDown className="w-4 h-4 ml-1" />
                                </button>
                              </div>

                              {showArticles && (
                                <div className="mt-4 space-y-2">
                                  {selectedCliffNote.articles.map(
                                    (article, idx) => (
                                      <div
                                        key={idx}
                                        className="px-3 py-2 rounded-md hover:bg-gray-50 transition-colors"
                                      >
                                        <a
                                          href={article.url}
                                          className="text-blue-600 hover:underline text-sm block"
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          {article.title}
                                        </a>
                                      </div>
                                    )
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                        {/* Auto-generated disclaimer and CTA */}
                        {!session && (
                          <div className=" bg-gradient-to-r from-green-50 to-green-50 mt-6 p-4 rounded-lg">
                            <p className="text-xs text-gray-500 leading-relaxed">
                              This digest is auto-generated. If you'd like a
                              version shaped around your interests (say, only
                              model updates and startup news, none of the
                              policy), written in your own style (skeptical like
                              a watchdog or creative like a poem? sure), and
                              sent to your inbox when it suits you, you can{" "}
                              <a
                                href="/landing"
                                className="text-green-600 hover:text-green-700 underline transition-colors duration-200 font-medium"
                              >
                                generate your own here
                              </a>
                              .
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        No detailed insights available for this topic
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No trending topics found for this date
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
